import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, Boxes, CornerDownLeft, LifeBuoy, Palette, Search } from 'lucide-react';
import type { Alert, Resource, Ticket } from '@itr/shared';
import { api } from '../api/client';
import { useDebounced } from './ui';
import { useTheme } from '../theme/ThemeProvider';

interface Command {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  icon: typeof Search;
  run: () => void;
}

/**
 * Command palette.
 *
 * Search hits the resource, alert and ticket endpoints in parallel, which is the
 * only navigation an engineer actually needs mid-incident: they know the name of
 * the thing that is broken, not which page it lives on.
 */
export function CommandPalette({
  open,
  onClose,
  nav,
}: {
  open: boolean;
  onClose: () => void;
  nav: Array<{ to: string; label: string; icon: typeof Search }>;
}) {
  const navigate = useNavigate();
  const { themes, setThemeId, setMode } = useTheme();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [results, setResults] = useState<{ resources: Resource[]; alerts: Alert[]; tickets: Ticket[] }>({
    resources: [],
    alerts: [],
    tickets: [],
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounced = useDebounced(query, 220);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setResults({ resources: [], alerts: [], tickets: [] });
      // Focus after the open animation starts, or Safari steals it back.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open || debounced.trim().length < 2) {
      setResults({ resources: [], alerts: [], tickets: [] });
      return;
    }
    let cancelled = false;

    void (async () => {
      const [resources, alerts, tickets] = await Promise.all([
        api.get<Resource[]>('/api/resources', { search: debounced, limit: 6 }).catch(() => []),
        api.get<Alert[]>('/api/alerts', { state: 'open', limit: 30 }).catch(() => []),
        api.get<Ticket[]>('/api/tickets', { search: debounced, limit: 5 }).catch(() => []),
      ]);
      if (cancelled) return;

      // Alerts have no search endpoint — filtering the open set client-side is
      // fine because the open set is small by definition.
      const needle = debounced.toLowerCase();
      setResults({
        resources,
        alerts: alerts.filter((a) => a.title.toLowerCase().includes(needle)).slice(0, 5),
        tickets,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [debounced, open]);

  const commands = useMemo<Command[]>(() => {
    const needle = query.trim().toLowerCase();
    const list: Command[] = [];

    for (const item of nav) {
      if (!needle || item.label.toLowerCase().includes(needle)) {
        list.push({
          id: `nav:${item.to}`,
          group: 'Go to',
          label: item.label,
          icon: item.icon,
          run: () => navigate(item.to),
        });
      }
    }

    for (const resource of results.resources) {
      list.push({
        id: `res:${resource.id}`,
        group: 'Resources',
        label: resource.name,
        sublabel: `${resource.kind} · ${resource.health}`,
        icon: Boxes,
        run: () => navigate(`/resources/${resource.id}`),
      });
    }

    for (const alert of results.alerts) {
      list.push({
        id: `alt:${alert.id}`,
        group: 'Alerts',
        label: alert.title,
        sublabel: alert.severity,
        icon: Bell,
        run: () => navigate(`/alerts/${alert.id}`),
      });
    }

    for (const ticket of results.tickets) {
      list.push({
        id: `tkt:${ticket.id}`,
        group: 'Tickets',
        label: `${ticket.reference} — ${ticket.subject}`,
        sublabel: ticket.state,
        icon: LifeBuoy,
        run: () => navigate(`/tickets/${ticket.id}`),
      });
    }

    // Theme switching from the palette, because "how do I make this dark" is the
    // single most common thing a new user hunts for in the settings page.
    if (needle.length >= 2 && ('theme'.includes(needle) || 'dark'.includes(needle) || 'light'.includes(needle))) {
      list.push(
        { id: 'mode:dark', group: 'Appearance', label: 'Switch to dark mode', icon: Palette, run: () => setMode('dark') },
        { id: 'mode:light', group: 'Appearance', label: 'Switch to light mode', icon: Palette, run: () => setMode('light') },
        { id: 'mode:system', group: 'Appearance', label: 'Follow system appearance', icon: Palette, run: () => setMode('system') },
        ...themes.map((theme) => ({
          id: `theme:${theme.id}`,
          group: 'Appearance',
          label: `Theme: ${theme.name}`,
          icon: Palette,
          run: () => setThemeId(theme.id),
        })),
      );
    }

    return list;
  }, [query, nav, results, navigate, themes, setThemeId, setMode]);

  useEffect(() => setActive(0), [commands.length]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % Math.max(1, commands.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + commands.length) % Math.max(1, commands.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const command = commands[active];
      if (command) {
        command.run();
        onClose();
      }
    }
  };

  let lastGroup = '';

  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="row" style={{ gap: 0, alignItems: 'center', paddingLeft: 16 }}>
          <Search size={16} style={{ color: 'var(--itr-text-subtle)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="palette-input grow"
            placeholder="Search resources, alerts, tickets, or jump to a page…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            style={{ paddingLeft: 10 }}
            aria-label="Search"
          />
        </div>

        <div className="palette-list" ref={listRef}>
          {commands.length === 0 ? (
            <div className="subtle" style={{ padding: '22px 12px', textAlign: 'center', fontSize: 13 }}>
              {query.trim().length < 2 ? 'Type at least two characters to search.' : `No matches for "${query}".`}
            </div>
          ) : (
            commands.map((command, i) => {
              const showGroup = command.group !== lastGroup;
              lastGroup = command.group;
              return (
                <div key={command.id}>
                  {showGroup && <div className="palette-group-label">{command.group}</div>}
                  <div
                    className="palette-item"
                    data-active={i === active}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      command.run();
                      onClose();
                    }}
                  >
                    <command.icon size={15} style={{ color: 'var(--itr-text-subtle)', flexShrink: 0 }} />
                    <span className="truncate grow">{command.label}</span>
                    {command.sublabel && <span className="palette-item-sub">{command.sublabel}</span>}
                    {i === active && <CornerDownLeft size={13} style={{ color: 'var(--itr-text-subtle)' }} />}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div
          className="row"
          style={{
            gap: 14,
            padding: '8px 14px',
            borderTop: 'var(--itr-border-w) solid var(--itr-border)',
            fontSize: 11.5,
            color: 'var(--itr-text-subtle)',
          }}
        >
          <span className="row" style={{ gap: 5 }}>
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> navigate
          </span>
          <span className="row" style={{ gap: 5 }}>
            <span className="kbd">↵</span> open
          </span>
          <span className="row" style={{ gap: 5 }}>
            <span className="kbd">esc</span> close
          </span>
        </div>
      </div>
    </>
  );
}
